// scheduledTriggerRuntime — P-105 follow-up: pure helpers som en framtida
// cron-runner-poller (pg_cron eller GitHub-Actions) använder för att
// bestämma när nästa run ska triggas.
//
// Runtime-arkitektur (designat, inte deploy:at här):
// 1. Poller kör var 60:e sekund via pg_cron:
//      SELECT id, schedule_kind, interval_seconds, cron_expression
//      FROM scheduled_trigger
//      WHERE disabled_at IS NULL AND next_run_at <= now()
// 2. För varje rad: POST mot run-workflow edge-fn (service-role-auth).
// 3. Efter success: UPDATE scheduled_trigger SET
//      last_run_at = now(),
//      next_run_at = computeNextRunAt(schedule),
//      trigger_count = trigger_count + 1.
//
// Denna modul är pure helpers; själva cron-runner:n landas som
// infrastruktur separat.

import { validateCronExpression } from '@/lib/cronValidation';

export interface ScheduleSpec {
  kind: 'interval' | 'cron';
  intervalSeconds?: number | null;
  cronExpression?: string | null;
}

/** Beräkna nästa fire-time från en schedule + nuvarande tid. Returnerar
 *  Date eller null om schedule är ogiltig. */
export function computeNextRunAt(
  schedule: ScheduleSpec,
  now: Date = new Date(),
): Date | null {
  if (schedule.kind === 'interval') {
    if (!schedule.intervalSeconds || schedule.intervalSeconds < 60) return null;
    return new Date(now.getTime() + schedule.intervalSeconds * 1000);
  }
  if (schedule.kind === 'cron') {
    if (!schedule.cronExpression) return null;
    const validation = validateCronExpression(schedule.cronExpression);
    if (!validation.ok) return null;
    // Minimal next-fire-computation för 6-field cron. Full impl
    // kräver timezone-awareness via Intl.DateTimeFormat; denna version
    // hanterar UTC och stegar 1 sekund i taget upp till 7 dagar framåt.
    return computeCronNext(schedule.cronExpression, now);
  }
  return null;
}

/** Minimal 6-field cron "next-fire" — stegvis forward-scan. O(max 7 days
 *  * 86400 sec) worst-case men tidig-exit vid match. Används för
 *  scheduled_trigger där triggers typiskt fire:as flera gånger per dag,
 *  så scan är billigt. */
function computeCronNext(expression: string, from: Date): Date | null {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 6) return null;
  const matchers = fields.map((f, idx) => compileCronField(f, idx));
  if (matchers.some((m) => m === null)) return null;

  // Start från NÄSTA sekund för att undvika re-fire av current.
  const start = new Date(from.getTime() + 1000);
  start.setMilliseconds(0);

  const MAX_SECONDS = 7 * 24 * 60 * 60;
  for (let offset = 0; offset < MAX_SECONDS; offset++) {
    const t = new Date(start.getTime() + offset * 1000);
    if (
      matchers[0]!(t.getUTCSeconds()) &&
      matchers[1]!(t.getUTCMinutes()) &&
      matchers[2]!(t.getUTCHours()) &&
      matchers[3]!(t.getUTCDate()) &&
      matchers[4]!(t.getUTCMonth() + 1) &&
      matchers[5]!(t.getUTCDay())
    ) {
      return t;
    }
  }
  return null;
}

type Matcher = (value: number) => boolean;

/** Kompilera ett cron-fält till en matcher-funktion. Stödjer
 *  \*, N, a-b, a,b,c, \*\/N. Returnerar null vid invalid. */
function compileCronField(field: string, fieldIdx: number): Matcher | null {
  const [min, max] = FIELD_RANGES[fieldIdx];
  if (field === '*') return () => true;
  if (field.startsWith('*/')) {
    const step = parseInt(field.slice(2), 10);
    if (!Number.isFinite(step) || step < 1) return null;
    return (v: number) => v >= min && v <= max && (v - min) % step === 0;
  }
  // Lista av enklare delar: "0,5,10" eller "0-5"
  const parts = field.split(',');
  const allowed = new Set<number>();
  for (const part of parts) {
    if (part.includes('-')) {
      const [a, b] = part.split('-').map((n) => parseInt(n, 10));
      if (!Number.isFinite(a) || !Number.isFinite(b) || a > b) return null;
      for (let i = Math.max(a, min); i <= Math.min(b, max); i++) allowed.add(i);
    } else if (part.includes('/')) {
      const [base, stepStr] = part.split('/');
      const step = parseInt(stepStr, 10);
      const baseN = base === '*' ? min : parseInt(base, 10);
      if (!Number.isFinite(step) || step < 1 || !Number.isFinite(baseN)) return null;
      for (let i = Math.max(baseN, min); i <= max; i += step) allowed.add(i);
    } else {
      const n = parseInt(part, 10);
      if (!Number.isFinite(n) || n < min || n > max) return null;
      allowed.add(n);
    }
  }
  return (v: number) => allowed.has(v);
}

// Field-ranges: sekund, minut, timme, dag, månad, veckodag (0=sön)
const FIELD_RANGES: Array<[number, number]> = [
  [0, 59],
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 6],
];

/** Pure helper: givet en ScheduleSpec + last-run-tidpunkt, returnera
 *  "is-due-for-run-now"-bool. Används av poller för att skippa rader
 *  där next_run_at är missing (krasch-recovery eller initial insert
 *  utan explicit next_run_at). */
export function isDueForRun(
  nextRunAt: string | Date | null,
  now: Date = new Date(),
): boolean {
  if (!nextRunAt) return false;
  const due = typeof nextRunAt === 'string' ? new Date(nextRunAt) : nextRunAt;
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() <= now.getTime();
}
